import type { ParseKeys } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { EmptyState } from '@/components/EmptyState';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { VideoCard } from '@/pages/Mail/components/VideoCard';
import { VideoMeetingGroup } from '@/types/components';
import type { VideoMeeting } from '@/types/video';

const FADE_RANGE_PX = 24;

const GROUP_LABELS: Record<VideoMeetingGroup, ParseKeys> = {
  [VideoMeetingGroup.Today]: 'mail.video.today',
  [VideoMeetingGroup.All]: 'mail.video.all',
};

interface Props {
  meetings: VideoMeeting[];
  isMobile: boolean;
  respondingId: string | null;
  onOpen: (id: string) => void;
  onJoin: (id: string) => void;
  onApprove: (id: string) => void;
  onDecline: (id: string) => void;
}

export const VideoList: React.FC<Props> = ({
  meetings,
  isMobile,
  respondingId,
  onOpen,
  onJoin,
  onApprove,
  onDecline,
}) => {
  const { t } = useTranslation();
  const { progress, handleScroll } = useScrollFadeProgress(FADE_RANGE_PX);

  const isEmpty = meetings.length === 0;

  const groups = [VideoMeetingGroup.Today, VideoMeetingGroup.All]
    .map((group) => ({
      group,
      items: meetings.filter((meeting) => meeting.group === group),
    }))
    .filter(({ items }) => items.length > 0);

  const content = groups.map(({ group, items }) => (
    <div key={group} className="flex flex-col">
      <p className="text-callout-16 px-4 pb-3 text-foreground md:border-b md:border-divider md:px-5 md:pb-4 md:text-subhead-14 md:text-muted-foreground">
        {t(GROUP_LABELS[group])}
      </p>
      <div className="flex flex-col gap-2 px-4 md:gap-3 md:px-5 md:pt-3">
        {items.map((meeting) => (
          <VideoCard
            key={meeting.id}
            meeting={meeting}
            isMobile={isMobile}
            isResponding={meeting.id === respondingId}
            onOpen={onOpen}
            onJoin={onJoin}
            onApprove={onApprove}
            onDecline={onDecline}
          />
        ))}
      </div>
    </div>
  ));

  const fadeOverlay = (
    <div
      style={{ opacity: progress }}
      className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-constant-black/6 to-transparent transition-opacity duration-300 ease-out"
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-card max-md:rounded-t-2xl max-md:shadow-card md:rounded-[20px]">
      {isEmpty && (
        <div className="flex flex-1 flex-col pb-8">
          <EmptyState description={t('mail.video.empty')} />
        </div>
      )}
      {!isEmpty && (
        <div className="relative flex min-h-0 flex-1 flex-col">
          {fadeOverlay}
          {isMobile && (
            <div
              onScroll={handleScroll}
              className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pt-4 pb-4"
            >
              {content}
            </div>
          )}
          {!isMobile && (
            <AutoHideScrollArea
              onScroll={handleScroll}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="flex flex-col gap-6 pt-5 pb-5">{content}</div>
            </AutoHideScrollArea>
          )}
        </div>
      )}
    </div>
  );
};
